import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScreenHeader } from "../../next/page/ScreenHeader";
import {
  FormAlert,
  FormDialog,
  FormField,
  FormGrid,
  FormSearchSelect,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import { useAuth } from "../../auth/AuthProvider";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { financeApi, formatShekels } from "../../api/financeApi";
import {
  financeStructureApi,
  type FinanceCategory,
} from "../../api/financeStructureApi";
import {
  nedarimApi,
  type NedarimMovement,
  type SubscriptionWithPeriod,
} from "../../api/nedarimApi";
import { toClientApiError } from "../../api/httpClient";
import { ListStatStrip } from "../../ui/ListStatStrip";
import { useTableLayoutPrefs } from "../../hooks/useTableLayoutPrefs";
import { useLookupOptions } from "../../ui/useLookupOptions";
import { useCustomerScope } from "../financeStructure/useCustomerScope";
import { SubscriptionCard } from "./SubscriptionCard";
import "../financeStructure/financeStructure.css";
import "../settings/nedarimConnection.css";

const STATUS_OPTIONS = [
  { value: "active", label: "פעיל" },
  { value: "paused", label: "מושהה" },
  { value: "draft", label: "טיוטה" },
  { value: "cancelled", label: "בוטל" },
  { value: "expired", label: "פג" },
] as const;

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

const PERIOD_LABELS: Record<string, string> = {
  paid: "שולם",
  unpaid: "לא שולם",
  failed: "נכשל",
  none: "ללא הו״ק",
};

type FilterOption = { id: string; name: string };
type ArrangementOption = { value: string; label: string };

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function statusTone(status: unknown): string {
  if (status === "active") return "ok";
  if (status === "paused") return "warn";
  if (status === "cancelled" || status === "expired") return "muted";
  return "info";
}

function periodTone(status: unknown): string {
  if (status === "paid") return "ok";
  if (status === "failed") return "failed";
  if (status === "unpaid") return "warn";
  return "muted";
}

function renderPeriodStatus(value: unknown) {
  const key = String(value ?? "none");
  return (
    <span className={`subscription-grid__pill subscription-grid__pill--${periodTone(key)}`}>
      {PERIOD_LABELS[key] ?? key}
    </span>
  );
}

function stopSelectBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

type StopTarget = {
  id: string;
  title: string;
  hasKeva: boolean;
  channel: string | null;
};

/** AR — מנויים ותשלומים + תנועות נדרים פלוס. */
export function SubscriptionsPaymentsPage() {
  const labels = useEntityLabels();
  const auth = useAuth();
  const { customers, loading: customersLoading, error: customersError } = useCustomerScope();
  const { prefs, savePrefs } = useTableLayoutPrefs("subscriptions.list");
  const {
    prefs: movPrefs,
    savePrefs: saveMovPrefs,
  } = useTableLayoutPrefs("nedarim.movements.list");
  const [tab, setTab] = useState<"subscriptions" | "movements">("subscriptions");
  const [billingPeriod, setBillingPeriod] = useState(currentPeriod);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithPeriod[]>([]);
  const [movements, setMovements] = useState<NedarimMovement[]>([]);
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [customerFilter, setCustomerFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionWithPeriod | null>(null);
  const [stopTarget, setStopTarget] = useState<StopTarget | null>(null);
  const [stopAction, setStopAction] = useState<"freeze" | "delete" | "local_only">(
    "freeze",
  );
  const [stopBusy, setStopBusy] = useState(false);
  const [detailMovement, setDetailMovement] = useState<NedarimMovement | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(() => new Set());
  const appliedDefaultHidden = useRef(false);
  const { options: paymentArrangementLookup } = useLookupOptions(
    "payment_arrangements",
  );

  const enabledFeatures = auth.activeTenant?.enabledFeatures ?? [];
  const arOn =
    enabledFeatures.includes("finance.ar") || enabledFeatures.length === 0;

  useEffect(() => {
    if (!arOn) return;
    void financeStructureApi
      .listCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [arOn]);

  const load = useCallback(async () => {
    if (!arOn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (tab === "subscriptions") {
        const result = await nedarimApi.listSubscriptions({
          billingPeriod,
          customerId: customerFilter || undefined,
          categoryId: categoryFilter || undefined,
        });
        setSubscriptions(result.items);
      } else {
        setMovements(
          await nedarimApi.listMovements({
            billingPeriod,
            customerId: customerFilter || undefined,
          }),
        );
      }
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [arOn, billingPeriod, categoryFilter, customerFilter, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (appliedDefaultHidden.current || prefs.hiddenColumnKeys) {
      return;
    }
    appliedDefaultHidden.current = true;
    void savePrefs({
      ...prefs,
      hiddenColumnKeys: [
        "id",
        "customerId",
        "nedarimKevaId",
        "createdAt",
        "updatedAt",
      ],
    });
  }, [prefs, savePrefs]);

  const customerById = useMemo(
    () => new Map(customers.map((customer) => [customer.id, customer])),
    [customers],
  );

  const categoryById = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );

  const customerOptions = useMemo<FilterOption[]>(
    () => [
      { id: "", name: `כל ${labels.customers}` },
      ...customers.map((c) => ({ id: c.id, name: c.name })),
    ],
    [customers, labels.customers],
  );

  const categoryOptions = useMemo<FilterOption[]>(
    () => [
      { id: "", name: "כל סוגי המנוי" },
      ...categories.map((c) => ({ id: c.id, name: c.name })),
    ],
    [categories],
  );

  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find((o) => o.id === customerFilter) ??
      customerOptions[0] ??
      null,
    [customerFilter, customerOptions],
  );

  const selectedCategoryOption = useMemo(
    () =>
      categoryOptions.find((o) => o.id === categoryFilter) ??
      categoryOptions[0] ??
      null,
    [categoryFilter, categoryOptions],
  );

  const arrangementOptions = useMemo(() => {
    const byValue = new Map<string, ArrangementOption>();
    byValue.set("", { value: "", label: "ללא הסדר" });
    for (const option of paymentArrangementLookup) {
      byValue.set(option.key, { value: option.key, label: option.labelHe });
    }
    for (const sub of subscriptions) {
      const raw = sub.paymentArrangement?.trim();
      if (!raw) continue;
      const known = paymentArrangementLookup.find(
        (o) => o.key === raw || o.labelHe === raw,
      );
      if (known) {
        byValue.set(known.key, { value: known.key, label: known.labelHe });
      } else if (!byValue.has(raw)) {
        byValue.set(raw, { value: raw, label: raw });
      }
    }
    return Array.from(byValue.values());
  }, [paymentArrangementLookup, subscriptions]);

  const patchSubscription = useCallback(
    async (
      id: string,
      patch: { status?: string; paymentArrangement?: string | null },
    ) => {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setError(null);
      const previous = subscriptions.find((sub) => sub.id === id);
      setSubscriptions((prev) =>
        prev.map((sub) =>
          sub.id === id
            ? {
                ...sub,
                ...(patch.status !== undefined ? { status: patch.status } : {}),
                ...(patch.paymentArrangement !== undefined
                  ? { paymentArrangement: patch.paymentArrangement }
                  : {}),
              }
            : sub,
        ),
      );
      try {
        const updated = await financeApi.updateSubscription(id, patch);
        setSubscriptions((prev) =>
          prev.map((sub) =>
            sub.id === id
              ? {
                  ...sub,
                  status: updated.status,
                  paymentArrangement: updated.paymentArrangement,
                  updatedAt: updated.updatedAt,
                }
              : sub,
          ),
        );
        setSelectedSubscription((current) =>
          current?.id === id
            ? {
                ...current,
                status: updated.status,
                paymentArrangement: updated.paymentArrangement,
                updatedAt: updated.updatedAt,
              }
            : current,
        );
      } catch (err) {
        if (previous) {
          setSubscriptions((prev) =>
            prev.map((sub) => (sub.id === id ? previous : sub)),
          );
        }
        setError(toClientApiError(err).message);
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [subscriptions],
  );

  const columns = useMemo(
    () => [
      { key: "customerName", title: labels.customer, sortable: true, filterable: true, minWidth: 180 },
      { key: "title", title: "מנוי", sortable: true, filterable: true, minWidth: 120 },
      {
        key: "categoryName",
        title: "סוג מנוי",
        sortable: true,
        filterable: true,
        minWidth: 120,
      },
      {
        key: "amountAgorot",
        title: "תעריף",
        sortable: true,
        filterable: true,
        minWidth: 110,
        render: (value: unknown) => `${formatShekels(Number(value) || 0)} ₪`,
      },
      {
        key: "status",
        title: "סטטוס",
        sortable: true,
        filterable: true,
        minWidth: 130,
        render: (value: unknown, row: Record<string, unknown>) => {
          const id = String(row.id ?? "");
          const current = String(value ?? "active");
          const busy = savingIds.has(id);
          return (
            <select
              className={`subscription-grid__select subscription-grid__select--${statusTone(current)}`}
              value={current}
              disabled={busy || !id}
              aria-label="סטטוס מנוי"
              onClick={stopSelectBubble}
              onMouseDown={stopSelectBubble}
              onChange={(event) => {
                event.stopPropagation();
                const next = event.target.value;
                if (!id || next === current) return;
                void patchSubscription(id, { status: next });
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {!STATUS_LABELS[current] && current ? (
                <option value={current}>{current}</option>
              ) : null}
            </select>
          );
        },
      },
      {
        key: "periodChargeStatus",
        title: `גבייה ${billingPeriod}`,
        sortable: true,
        filterable: true,
        minWidth: 120,
        render: renderPeriodStatus,
      },
      {
        key: "paymentArrangement",
        title: "הסדר תשלום",
        sortable: true,
        filterable: true,
        minWidth: 180,
        render: (value: unknown, row: Record<string, unknown>) => {
          const id = String(row.id ?? "");
          const raw = String(value ?? "").trim();
          const matched = arrangementOptions.find(
            (o) => o.value === raw || o.label === raw,
          );
          const selectValue = matched?.value ?? raw;
          const busy = savingIds.has(id);
          const options =
            selectValue && !arrangementOptions.some((o) => o.value === selectValue)
              ? [...arrangementOptions, { value: selectValue, label: selectValue }]
              : arrangementOptions;
          return (
            <select
              className="subscription-grid__select subscription-grid__select--info"
              value={selectValue}
              disabled={busy || !id}
              aria-label="הסדר תשלום"
              onClick={stopSelectBubble}
              onMouseDown={stopSelectBubble}
              onChange={(event) => {
                event.stopPropagation();
                const next = event.target.value;
                if (!id || next === selectValue) return;
                void patchSubscription(id, {
                  paymentArrangement: next || null,
                });
              }}
            >
              {options.map((option) => (
                <option key={option.value || "__empty"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        key: "nedarimChannel",
        title: "ערוץ נדרים",
        sortable: true,
        filterable: true,
        minWidth: 110,
        render: (value: unknown) =>
          value === "credit" ? "אשראי" : value === "bank" ? "מס״ב" : "—",
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 160,
        render: (_: unknown, row: Record<string, unknown>) => {
          const status = String(row.status ?? "");
          const hasKeva = Boolean(row.nedarimKevaId);
          if (status === "paused" && hasKeva) {
            return (
              <button
                type="button"
                className="crm-btn crm-btn--secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  void (async () => {
                    try {
                      const action =
                        row.nedarimChannel === "bank" ? "restore" : "enable";
                      await nedarimApi.stopSubscription(String(row.id), action);
                      await load();
                    } catch (err) {
                      setError(toClientApiError(err).message);
                    }
                  })();
                }}
              >
                הפעלה מחדש
              </button>
            );
          }
          return (
            <button
              type="button"
              className="crm-btn crm-btn--secondary"
              onClick={(e) => {
                e.stopPropagation();
                setStopAction(hasKeva ? "freeze" : "local_only");
                setStopTarget({
                  id: String(row.id),
                  title: String(row.title ?? ""),
                  hasKeva,
                  channel: row.nedarimChannel ? String(row.nedarimChannel) : null,
                });
              }}
            >
              עצירת מנוי
            </button>
          );
        },
      },
    ],
    [
      arrangementOptions,
      billingPeriod,
      labels.customer,
      load,
      patchSubscription,
      savingIds,
    ],
  );

  const rows = useMemo(
    () =>
      subscriptions.map((sub) => ({
        ...sub,
        customerName: customerById.get(sub.customerId)?.name ?? sub.customerId,
        categoryName: sub.categoryId
          ? categoryById.get(sub.categoryId)?.name ?? "—"
          : "—",
      })),
    [categoryById, customerById, subscriptions],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (statusFilter && row.status !== statusFilter) return false;
        return true;
      }),
    [rows, statusFilter],
  );

  const movementColumns = useMemo(
    () => [
      {
        key: "occurredOn",
        title: "תאריך",
        sortable: true,
        filterable: true,
        minWidth: 110,
        render: (value: unknown) =>
          value ? new Date(String(value)).toLocaleDateString("he-IL") : "—",
      },
      {
        key: "billingPeriod",
        title: "תקופה",
        sortable: true,
        filterable: true,
        minWidth: 90,
      },
      {
        key: "kind",
        title: "סוג",
        sortable: true,
        filterable: true,
        minWidth: 100,
        render: (value: unknown) => {
          const map: Record<string, string> = {
            transaction: "עסקה",
            mandate: "הו״ק",
            charge: "חיוב",
            siruv: "סירוב",
          };
          return map[String(value)] ?? String(value);
        },
      },
      {
        key: "channel",
        title: "ערוץ",
        sortable: true,
        filterable: true,
        minWidth: 90,
        render: (value: unknown) =>
          value === "credit" ? "אשראי" : value === "bank" ? "מס״ב" : "—",
      },
      {
        key: "amountAgorot",
        title: "סכום",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) => `${formatShekels(Number(value) || 0)} ₪`,
      },
      {
        key: "status",
        title: "סטטוס",
        sortable: true,
        filterable: true,
        minWidth: 100,
        render: (value: unknown) => {
          const map: Record<string, string> = {
            success: "הצלחה",
            failed: "נכשל",
            pending: "ממתין",
            cancelled: "בוטל",
          };
          const key = String(value);
          return (
            <span
              className={`subscription-grid__pill subscription-grid__pill--${
                key === "success" ? "ok" : key === "failed" ? "failed" : "warn"
              }`}
            >
              {map[key] ?? key}
            </span>
          );
        },
      },
      { key: "payerName", title: "שם", sortable: true, filterable: true, minWidth: 140 },
      { key: "groupe", title: "קבוצה", sortable: true, filterable: true, minWidth: 120 },
      {
        key: "subscriptionLabel",
        title: "מנוי מקושר",
        sortable: true,
        filterable: true,
        minWidth: 120,
      },
    ],
    [],
  );

  const movementRows = useMemo(
    () =>
      movements.map((m) => ({
        ...m,
        subscriptionLabel: m.subscriptionId
          ? subscriptions.find((s) => s.id === m.subscriptionId)?.title ||
            m.subscriptionId.slice(0, 8)
          : "ללא שיוך",
        customerName: m.customerId
          ? customerById.get(m.customerId)?.name ?? m.customerId
          : "—",
      })),
    [customerById, movements, subscriptions],
  );

  const stats = useMemo(
    () => ({
      total: subscriptions.length,
      active: subscriptions.filter((sub) => sub.status === "active").length,
      paid: subscriptions.filter((sub) => sub.periodChargeStatus === "paid").length,
    }),
    [subscriptions],
  );

  function openSubscriptionCard(row: Record<string, unknown>) {
    const sub = subscriptions.find((s) => s.id === String(row.id));
    if (sub) setSelectedSubscription(sub);
    return true;
  }

  async function confirmStop() {
    if (!stopTarget) return;
    setStopBusy(true);
    setError(null);
    try {
      await nedarimApi.stopSubscription(stopTarget.id, stopAction);
      setStopTarget(null);
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setStopBusy(false);
    }
  }

  return (
    <div className="page-section page-section--list">
      <ScreenHeader sub="מנוי בכל שורה, חודש גבייה, סטטוס תשלום, הו״ק ותנועות מנדרים פלוס" />
      {!arOn ? (
        <p className="form-error">מודול מנויים ותשלומים אינו מופעל לארגון זה.</p>
      ) : null}

      <div className="nedarim-subs-tabs" role="tablist" aria-label="מנויים ותנועות">
        <button
          type="button"
          role="tab"
          className={`nedarim-subs-tabs__btn${tab === "subscriptions" ? " is-active" : ""}`}
          aria-selected={tab === "subscriptions"}
          onClick={() => setTab("subscriptions")}
        >
          מנויים
        </button>
        <button
          type="button"
          role="tab"
          className={`nedarim-subs-tabs__btn${tab === "movements" ? " is-active" : ""}`}
          aria-selected={tab === "movements"}
          onClick={() => setTab("movements")}
        >
          תנועות מנדרים פלוס
        </button>
      </div>

      <div className="nedarim-period-bar">
        <FormField
          label="חודש גבייה"
          type="month"
          value={billingPeriod}
          onChange={setBillingPeriod}
        />
        <FormSearchSelect
          label={labels.customer}
          options={customerOptions}
          value={selectedCustomerOption}
          onChange={(val: unknown) => {
            const opt = val as FilterOption | null;
            setCustomerFilter(opt?.id ?? "");
          }}
          getOptionLabel={(o) => (o as FilterOption)?.name || ""}
          isOptionEqual={(a, b) =>
            (a as FilterOption | null)?.id === (b as FilterOption | null)?.id
          }
          placeholder={`סינון לפי ${labels.customer}…`}
        />
        {tab === "subscriptions" ? (
          <FormSearchSelect
            label="סוג מנוי"
            options={categoryOptions}
            value={selectedCategoryOption}
            onChange={(val: unknown) => {
              const opt = val as FilterOption | null;
              setCategoryFilter(opt?.id ?? "");
            }}
            getOptionLabel={(o) => (o as FilterOption)?.name || ""}
            isOptionEqual={(a, b) =>
              (a as FilterOption | null)?.id === (b as FilterOption | null)?.id
            }
            placeholder="סינון לפי סוג מנוי…"
          />
        ) : null}
      </div>

      {loading || customersLoading ? <p>טוען…</p> : null}
      {error || customersError ? (
        <p className="form-error">{error ?? customersError}</p>
      ) : null}

      {!loading && !customersLoading && arOn && tab === "subscriptions" ? (
        <>
          <ListStatStrip
            title="סיכום מנויים"
            activeKey={statusFilter}
            onSelect={setStatusFilter}
            columns={3}
            cards={[
              {
                key: null,
                label: "סה״כ מנויים",
                value: stats.total,
                color: "#1976d2",
                iconName: "wallet",
              },
              {
                key: "active",
                label: "פעילים",
                value: stats.active,
                color: "#2e7d32",
                iconName: "check",
              },
              {
                key: null,
                label: `שולם ${billingPeriod}`,
                value: stats.paid,
                color: "#0f766e",
                iconName: "check",
              },
            ]}
          />

          {subscriptions.length === 0 ? (
            <FormAlert severity="info">אין מנויים להצגה.</FormAlert>
          ) : (
            <div className="page-section__table-surface">
              <TableKit
                columns={columns}
                data={filteredRows}
                loading={false}
                clientSideMode
                emptyMessage="אין מנויים להצגה"
                exportFileName="subscriptions"
                showExport
                showFilters
                showColumnPicker
                showAdvancedSettings
                onRowClick={openSubscriptionCard}
                onOpenCard={openSubscriptionCard}
                columnOrder={(prefs.columnOrder as string[] | undefined) ?? undefined}
                onColumnOrderChange={(next: string[]) => {
                  void savePrefs({ ...prefs, columnOrder: next });
                }}
                hiddenColumnKeys={(prefs.hiddenColumnKeys as string[] | undefined) ?? undefined}
                onHiddenColumnKeysChange={(next: string[]) => {
                  void savePrefs({ ...prefs, hiddenColumnKeys: next });
                }}
              />
            </div>
          )}
        </>
      ) : null}

      {!loading && arOn && tab === "movements" ? (
        movements.length === 0 ? (
          <FormAlert severity="info">
            אין תנועות לחודש זה. הריצו סנכרון בהגדרות או המתינו ל-Webhook.
          </FormAlert>
        ) : (
          <div className="page-section__table-surface">
            <TableKit
              columns={movementColumns}
              data={movementRows}
              loading={false}
              clientSideMode
              emptyMessage="אין תנועות"
              exportFileName="nedarim-movements"
              showExport
              showFilters
              showColumnPicker
              onRowClick={(row) => {
                setDetailMovement(row as unknown as NedarimMovement);
                return true;
              }}
              columnOrder={(movPrefs.columnOrder as string[] | undefined) ?? undefined}
              onColumnOrderChange={(next: string[]) => {
                void saveMovPrefs({ ...movPrefs, columnOrder: next });
              }}
              hiddenColumnKeys={
                (movPrefs.hiddenColumnKeys as string[] | undefined) ?? undefined
              }
              onHiddenColumnKeysChange={(next: string[]) => {
                void saveMovPrefs({
                  ...movPrefs,
                  hiddenColumnKeys: next,
                });
              }}
            />
          </div>
        )
      ) : null}

      {selectedSubscription ? (
        <SubscriptionCard
          subscription={selectedSubscription}
          customerName={
            customerById.get(selectedSubscription.customerId)?.name ??
            selectedSubscription.customerId
          }
          categoryName={
            selectedSubscription.categoryId
              ? categoryById.get(selectedSubscription.categoryId)?.name ?? "—"
              : "—"
          }
          onClose={() => setSelectedSubscription(null)}
        />
      ) : null}

      <FormDialog
        open={Boolean(stopTarget)}
        onClose={() => {
          if (!stopBusy) setStopTarget(null);
        }}
        size="sm"
      >
        <div className="page-section page-section--prose">
          <h3>עצירת מנוי</h3>
          {stopTarget ? (
            <>
              <p>
                עצירת «{stopTarget.title}»
                {stopTarget.hasKeva
                  ? " — בחרו פעולה בנדרים פלוס:"
                  : " (ללא הו״ק מקושר — עצירה מקומית בלבד)."}
              </p>
              {stopTarget.hasKeva ? (
                <FormGrid cols={1}>
                  <label>
                    <input
                      type="radio"
                      name="stopAction"
                      checked={stopAction === "freeze"}
                      onChange={() => setStopAction("freeze")}
                    />{" "}
                    הקפאה בנדרים (ניתן להפעיל מחדש)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="stopAction"
                      checked={stopAction === "delete"}
                      onChange={() => setStopAction("delete")}
                    />{" "}
                    מחיקה בנדרים (סופי)
                  </label>
                </FormGrid>
              ) : null}
              {error ? <FormAlert severity="error">{error}</FormAlert> : null}
              <div className="nedarim-connection__actions">
                <button
                  type="button"
                  className="crm-btn crm-btn--secondary"
                  disabled={stopBusy}
                  onClick={() => setStopTarget(null)}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="crm-btn crm-btn--primary"
                  disabled={stopBusy}
                  onClick={() => void confirmStop()}
                >
                  {stopBusy
                    ? "מבצע…"
                    : stopAction === "delete"
                      ? "מחיקה"
                      : stopAction === "local_only"
                        ? "השהיה מקומית"
                        : "הקפאה"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </FormDialog>

      <FormDialog
        open={Boolean(detailMovement)}
        onClose={() => setDetailMovement(null)}
        size="md"
      >
        <div className="page-section page-section--prose">
          <h3>פרטי תנועת נדרים</h3>
          {detailMovement ? (
            <FormGrid cols={2}>
              <FormField
                label="מזהה עסקה"
                value={detailMovement.nedarimTransactionId ?? "—"}
                disabled
                onChange={() => undefined}
              />
              <FormField
                label="מזהה הו״ק"
                value={detailMovement.nedarimKevaId ?? "—"}
                disabled
                onChange={() => undefined}
              />
              <FormField
                label="שם"
                value={detailMovement.payerName ?? "—"}
                disabled
                onChange={() => undefined}
              />
              <FormField
                label="טלפון"
                value={detailMovement.payerPhone ?? "—"}
                disabled
                onChange={() => undefined}
              />
              <FormField
                label="סיבת כשל"
                value={detailMovement.failureReason ?? "—"}
                disabled
                onChange={() => undefined}
              />
              <FormField
                label="מקור"
                value={detailMovement.source}
                disabled
                onChange={() => undefined}
              />
            </FormGrid>
          ) : null}
          <div className="nedarim-connection__actions">
            <button
              type="button"
              className="crm-btn crm-btn--primary"
              onClick={() => setDetailMovement(null)}
            >
              סגור
            </button>
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
